import { useState, useEffect, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type IOSBrowser = "safari" | "chrome" | "firefox" | "edge" | "other";

interface UseInstallPromptReturn {
  isInstallable: boolean;
  showPrompt: boolean;
  isIOS: boolean;
  iosBrowser: IOSBrowser;
  isStandalone: boolean;
  showIOSModal: boolean;
  promptInstall: () => Promise<void>;
  dismiss: () => void;
  openIOSModal: () => void;
  closeIOSModal: () => void;
}

const VISIT_COUNT_KEY = "br_visit_count";
const DISMISSED_KEY = "br_a2hs_dismissed";
const IOS_SESSION_DISMISSED_KEY = "br_a2hs_dismissed_session";
const IOS_REMINDER_COUNT_KEY = "br_a2hs_reminder_count";

function detectIOSDevice(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}

function detectStandalone(): boolean {
  return (
    ("standalone" in navigator &&
      (navigator as { standalone?: boolean }).standalone === true) ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

export function detectIOSBrowser(): IOSBrowser {
  const ua = navigator.userAgent;
  if (/CriOS/.test(ua)) return "chrome";
  if (/FxiOS/.test(ua)) return "firefox";
  if (/EdgiOS/.test(ua)) return "edge";
  if (/Safari/.test(ua) && !/Chrome/.test(ua)) return "safari";
  return "other";
}

export function useInstallPrompt(): UseInstallPromptReturn {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [visitCount, setVisitCount] = useState(0);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [iosBrowser, setIOSBrowser] = useState<IOSBrowser>("other");
  const [iosModalOpen, setIOSModalOpen] = useState(false);

  useEffect(() => {
    const iosDevice = detectIOSDevice();
    const standalone = detectStandalone();
    setIsIOS(iosDevice && !standalone);
    setIsStandalone(standalone);

    if (iosDevice) {
      setIOSBrowser(detectIOSBrowser());
    }

    // If standalone, clear all dismiss flags and never show anything
    if (standalone) {
      localStorage.removeItem(DISMISSED_KEY);
      sessionStorage.removeItem(IOS_SESSION_DISMISSED_KEY);
      localStorage.removeItem(IOS_REMINDER_COUNT_KEY);
      setIsDismissed(true);
      return;
    }

    // Check dismiss state — iOS uses session + reminder logic, Android uses permanent
    if (iosDevice) {
      const permanentDismissed =
        localStorage.getItem(DISMISSED_KEY) === "true";
      const sessionDismissed =
        sessionStorage.getItem(IOS_SESSION_DISMISSED_KEY) === "true";

      if (permanentDismissed || sessionDismissed) {
        setIsDismissed(true);
      }
    } else {
      const dismissed = localStorage.getItem(DISMISSED_KEY) === "true";
      if (dismissed) {
        setIsDismissed(true);
      }
    }

    // Increment visit count
    const currentCount = parseInt(
      localStorage.getItem(VISIT_COUNT_KEY) || "0",
      10
    );
    const newCount = currentCount + 1;
    localStorage.setItem(VISIT_COUNT_KEY, String(newCount));
    setVisitCount(newCount);

    // Listen for the beforeinstallprompt event (Chrome/Android)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const promptInstall = async (): Promise<void> => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  const dismiss = (): void => {
    if (isIOS) {
      // Check if this is a gentle reminder dismiss (permanent) or first dismiss (session only)
      const reminderCount = parseInt(
        localStorage.getItem(IOS_REMINDER_COUNT_KEY) || "0",
        10
      );
      if (reminderCount >= 3) {
        // Dismissing the gentle reminder → permanent
        localStorage.setItem(DISMISSED_KEY, "true");
      } else {
        // First dismiss → session only, increment reminder count
        sessionStorage.setItem(IOS_SESSION_DISMISSED_KEY, "true");
        localStorage.setItem(
          IOS_REMINDER_COUNT_KEY,
          String(reminderCount + 1)
        );
      }
    } else {
      // Android: permanent dismiss
      localStorage.setItem(DISMISSED_KEY, "true");
    }
    setIsDismissed(true);
    setIOSModalOpen(false);
  };

  const openIOSModal = useCallback(() => {
    setIOSModalOpen(true);
  }, []);

  const closeIOSModal = useCallback(() => {
    setIOSModalOpen(false);
  }, []);

  // Android: show native install prompt after 2nd visit (unchanged)
  const showNativePrompt =
    isInstallable && visitCount >= 2 && !isDismissed;

  // iOS gentle reminder banner (after 3+ dismissed sessions)
  const reminderCount = parseInt(
    localStorage.getItem(IOS_REMINDER_COUNT_KEY) || "0",
    10
  );
  const isGentleReminder = isIOS && reminderCount >= 3 && !isDismissed;

  // showPrompt: Android banner OR iOS gentle reminder only
  // iOS modal is triggered explicitly, not via showPrompt
  const showPrompt = showNativePrompt || isGentleReminder;

  // iOS modal only opens via explicit openIOSModal() calls — never auto-opens
  const showIOSModal = iosModalOpen && isIOS && !isStandalone;

  return {
    isInstallable,
    showPrompt,
    isIOS,
    iosBrowser,
    isStandalone,
    showIOSModal,
    promptInstall,
    dismiss,
    openIOSModal,
    closeIOSModal,
  };
}
